import { useEffect } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Extra classes merged onto the sheet panel (e.g. a different top radius or
   *  an accessible label). Optional — omitted callers keep the default shell. */
  panelClassName?: string;
  /** Accessible name for the dialog (aria-label). */
  ariaLabel?: string;
}

export function BottomSheet({
  open,
  onClose,
  children,
  panelClassName,
  ariaLabel,
}: BottomSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Prerender (scripts/prerender.mjs) runs this module without a DOM. `open` is
  // false there, but guard anyway so document is never touched on the server.
  if (!open || typeof document === "undefined") return null;

  // Portal to <body>. The panel is `fixed inset-0`, which only resolves against
  // the VIEWPORT while no ancestor establishes a containing block — and
  // transform, filter, backdrop-filter, perspective, contain and will-change all
  // do. ReadingPage renders the share sheet inside its sticky `backdrop-blur-md`
  // header, so the sheet sized itself against a 63-77px header instead of the
  // screen and its panel was crushed from ~674px of content down to ~71px, at
  // every width including phones. Portaling out of that subtree is the durable
  // fix: no styling an ancestor picks up later can trap it again.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        className={`relative bg-bg-surface border-t border-[color:var(--hairline)] rounded-t-2xl p-6 pb-10 max-h-[80vh] overflow-y-auto ${panelClassName ?? ""}`}
      >
        <div className="w-10 h-1 rounded-full bg-[color:var(--hairline-strong)] mx-auto mb-4" />
        {children}
      </div>
    </div>,
    document.body
  );
}
